import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { clsx } from 'clsx';

export type ButtonVariant = 'primary' | 'normal' | 'bare' | 'menu';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  children?: ReactNode;
};

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-button-primary-background text-button-primary-text border border-button-primary-border hover:bg-button-primary-background-hover disabled:bg-button-primary-disabled-background disabled:text-button-primary-disabled-text',
  normal:
    'bg-button-normal-background text-button-normal-text border border-button-normal-border hover:bg-button-normal-background-hover shadow-menu disabled:text-button-normal-disabled-text',
  bare:
    'bg-transparent text-button-bare-text border border-transparent hover:bg-button-bare-background-hover',
  menu:
    'bg-button-menu-background text-button-menu-text border border-button-menu-border hover:bg-button-menu-background-hover',
};

/** Matches upstream's `Button` variants (primary / normal / bare / menu). */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { variant = 'normal', size = 'md', icon, children, className, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        className={clsx(
          'inline-flex shrink-0 items-center gap-1.5 rounded-lg font-medium outline-none transition-colors',
          'focus-visible:outline focus-visible:outline-2',
          size === 'sm' ? 'h-8 px-2.5 text-sm' : 'h-9 px-3.5 text-md',
          variants[variant],
          className,
        )}
        {...rest}
      >
        {icon}
        {children}
      </button>
    );
  },
);
